/**
 * 图表数据处理工具函数
 */

export interface ProcessChartDataOptions {
  records: any[];
  chartSpec: any;
  xAxisField?: string;
  yAxisField?: string;
  seriesField?: string | null;
}

/**
 * 处理图表数据的通用函数
 * 支持聚合、分组等操作
 */
export const processChartData = (options: ProcessChartDataOptions): any[] => {
  const { records, chartSpec, xAxisField, yAxisField, seriesField } = options;
  
  // 如果传入了具体字段，使用传入的字段；否则从 chartSpec 中提取
  let xField = xAxisField || chartSpec?.xField || chartSpec?.categoryField;
  let yField = yAxisField || chartSpec?.yField || chartSpec?.valueField;
  const seriesFieldValue = seriesField || chartSpec?.seriesField;

  // 处理xField为数组的情况
  if (Array.isArray(xField)) {
    xField = xField[0];
    console.log('xField是数组格式，取第一个元素:', xField);
  }

  // 处理yField为数组的情况
  if (Array.isArray(yField)) {
    yField = yField[0];
    console.log('yField是数组格式，取第一个元素:', yField);
  }

  // 确保字段为字符串类型
  if (!yField || typeof yField !== 'string') {
    console.warn('Y轴字段无效:', yField);
    return records;
  }

  if (!xField || typeof xField !== 'string') {
    console.warn('X轴字段无效:', xField);
    return records;
  }

  // 检查字段是否在数据集中存在
  const firstRecord = records[0] || {};
  const dataFields = Object.keys(firstRecord);
  const isYFieldExists = dataFields.includes(yField);
  const isXFieldExists = dataFields.includes(xField);

  console.log(`检查Y轴字段 "${yField}" 是否存在:`, isYFieldExists);
  console.log(`检查X轴字段 "${xField}" 是否存在:`, isXFieldExists);

  // 处理复合字段的辅助函数
  const getFieldValue = (record: any, field: string | string[]) => {
    if (Array.isArray(field)) {
      return field.map(f => record[f]);
    }
    return record[field];
  };

  // 解析AI生成的聚合字段
  const parseAggregateField = (yField: string) => {
    if (typeof yField !== 'string') {
      return null;
    }

    if (yField.includes('_')) {
      const parts = yField.split('_');
      if (parts.length >= 2) {
        const aggregateType = parts[0].toLowerCase();
        const targetField = parts.slice(1).join('_');

        if (dataFields.includes(targetField)) {
          return { aggregateType, targetField };
        }
      }
    }

    // 检查是否是常见的计数字段
    const countPatterns = ['count', '计数', '数量', '记录行数'];
    if (countPatterns.some(pattern => yField.toLowerCase().includes(pattern))) {
      return { aggregateType: 'count', targetField: null };
    }

    return null;
  };

  // 如果Y轴字段不存在于数据集中，需要进行聚合计算
  if (!isYFieldExists) {
    console.log(`Y轴字段 "${yField}" 不存在于数据集中，尝试解析聚合字段`);

    const parseResult = parseAggregateField(yField);

    if (parseResult) {
      const { aggregateType, targetField } = parseResult;
      console.log(`解析结果: 聚合类型=${aggregateType}, 目标字段=${targetField}`);

      switch (aggregateType) {
        case 'count':
          if (targetField && dataFields.includes(targetField)) {
            if (typeof firstRecord[targetField] === 'number') {
              return aggregateDataForSum(records, xField, yField, seriesFieldValue, targetField, getFieldValue);
            }
          }
          return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);

        case 'sum':
        case 'total':
          if (targetField && dataFields.includes(targetField)) {
            return aggregateDataForSum(records, xField, yField, seriesFieldValue, targetField, getFieldValue);
          } else {
            console.warn(`目标字段 "${targetField}" 不存在于数据集中，改为计数聚合`);
            return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
          }

        case 'avg':
        case 'average':
        case 'mean':
          if (targetField && dataFields.includes(targetField)) {
            return aggregateDataForAvg(records, xField, yField, seriesFieldValue, targetField, getFieldValue);
          } else {
            console.warn(`目标字段 "${targetField}" 不存在于数据集中，改为计数聚合`);
            return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
          }

        case 'max':
        case 'maximum':
          if (targetField && dataFields.includes(targetField)) {
            return aggregateDataForMax(records, xField, yField, seriesFieldValue, targetField, getFieldValue);
          } else {
            console.warn(`目标字段 "${targetField}" 不存在于数据集中，改为计数聚合`);
            return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
          }

        case 'min':
        case 'minimum':
          if (targetField && dataFields.includes(targetField)) {
            return aggregateDataForMin(records, xField, yField, seriesFieldValue, targetField, getFieldValue);
          } else {
            console.warn(`目标字段 "${targetField}" 不存在于数据集中，改为计数聚合`);
            return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
          }

        default:
          console.warn(`未识别的聚合类型 "${aggregateType}"，按计数处理`);
          return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
      }
    } else {
      // 无法解析聚合字段，按原来的逻辑判断
      if (yField === '记录行数' || yField.includes('count') || yField.includes('计数') || yField.includes('数量')) {
        return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
      } else {
        // 尝试找到第一个数值字段进行求和
        const numericFields = dataFields.filter(field => typeof firstRecord[field] === 'number');
        if (numericFields.length > 0) {
          return aggregateDataForSum(records, xField, yField, seriesFieldValue, numericFields[0], getFieldValue);
        } else {
          return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
        }
      }
    }
  }

  // Y轴字段存在于数据集中的处理逻辑
  if (yField === '记录行数') {
    return aggregateDataForCount(records, xField, yField, seriesFieldValue, getFieldValue);
  } else {
    // Y轴字段存在于数据集中，检查是否需要聚合
    const originalXField = chartSpec?.xField || chartSpec?.categoryField;
    const originalYField = chartSpec?.yField || chartSpec?.valueField;
    const needsAggregation = Array.isArray(originalXField) || Array.isArray(originalYField) || seriesFieldValue;

    if (needsAggregation && typeof firstRecord[yField] === 'number') {
      console.log(`Y轴字段 "${yField}" 存在于数据集中且为数值字段，进行求和聚合`);
      return aggregateDataForSum(records, xField, yField, seriesFieldValue, yField, getFieldValue);
    } else {
      // 直接映射数据
      return records.map(record => {
        const result: any = {};

        if (xField) {
          result[xField] = record[xField];
        }

        if (yField) {
          result[yField] = record[yField];
        }

        if (seriesFieldValue && seriesFieldValue !== yField) {
          if (Array.isArray(seriesFieldValue)) {
            const seriesValues = getFieldValue(record, seriesFieldValue);
            seriesFieldValue.forEach((field, idx) => {
              result[field] = seriesValues[idx];
            });
          } else {
            result[seriesFieldValue] = record[seriesFieldValue];
          }
        }

        return result;
      });
    }
  }
};

// 聚合函数实现
function aggregateDataForCount(
  records: any[],
  xField: string,
  yField: string,
  seriesField: string | undefined,
  getFieldValue: Function
) {
  if (!xField || !yField) {
    console.warn('聚合函数参数无效:', { xField, yField });
    return records;
  }

  if (seriesField && seriesField !== yField) {
    const groupedData: { [key: string]: { [series: string]: number } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const seriesValue = String(record[seriesField] || '未知');

      if (!groupedData[xValue]) {
        groupedData[xValue] = {};
      }
      groupedData[xValue][seriesValue] = (groupedData[xValue][seriesValue] || 0) + 1;
    });

    const processedData: any[] = [];
    Object.entries(groupedData).forEach(([xValue, seriesData]) => {
      Object.entries(seriesData).forEach(([seriesValue, count]) => {
        const dataItem: any = {
          [xField]: xValue,
          [yField]: count,
          [seriesField]: seriesValue
        };
        processedData.push(dataItem);
      });
    });

    return processedData;
  } else {
    const groupedData: { [key: string]: number } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      groupedData[xValue] = (groupedData[xValue] || 0) + 1;
    });

    return Object.entries(groupedData).map(([xValue, count]) => ({
      [xField]: xValue,
      [yField]: count
    }));
  }
}

function aggregateDataForSum(
  records: any[],
  xField: string,
  yField: string,
  seriesField: string | undefined,
  targetField: string,
  getFieldValue: Function
) {
  if (seriesField && seriesField !== yField) {
    const groupedData: { [key: string]: { [series: string]: number } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const seriesValue = String(record[seriesField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = {};
      }
      groupedData[xValue][seriesValue] = (groupedData[xValue][seriesValue] || 0) + numValue;
    });

    const result: any[] = [];
    Object.entries(groupedData).forEach(([xValue, seriesData]) => {
      Object.entries(seriesData).forEach(([seriesValue, sum]) => {
        const dataItem: any = {
          [xField]: xValue,
          [yField]: sum,
          [seriesField]: seriesValue
        };
        result.push(dataItem);
      });
    });

    return result;
  } else {
    const groupedData: { [key: string]: number } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const numValue = Number(record[targetField]) || 0;
      groupedData[xValue] = (groupedData[xValue] || 0) + numValue;
    });

    return Object.entries(groupedData).map(([xValue, sum]) => ({
      [xField]: xValue,
      [yField]: sum
    }));
  }
}

function aggregateDataForAvg(
  records: any[],
  xField: string,
  yField: string,
  seriesField: string | undefined,
  targetField: string,
  getFieldValue: Function
) {
  if (seriesField && seriesField !== yField) {
    const groupedData: { [key: string]: { [series: string]: { sum: number; count: number } } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const seriesValue = String(record[seriesField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = {};
      }
      if (!groupedData[xValue][seriesValue]) {
        groupedData[xValue][seriesValue] = { sum: 0, count: 0 };
      }

      groupedData[xValue][seriesValue].sum += numValue;
      groupedData[xValue][seriesValue].count += 1;
    });

    const result: any[] = [];
    Object.entries(groupedData).forEach(([xValue, seriesData]) => {
      Object.entries(seriesData).forEach(([seriesValue, { sum, count }]) => {
        const dataItem: any = {
          [xField]: xValue,
          [yField]: count > 0 ? sum / count : 0,
          [seriesField]: seriesValue
        };
        result.push(dataItem);
      });
    });

    return result;
  } else {
    const groupedData: { [key: string]: { sum: number; count: number } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = { sum: 0, count: 0 };
      }

      groupedData[xValue].sum += numValue;
      groupedData[xValue].count += 1;
    });

    return Object.entries(groupedData).map(([xValue, { sum, count }]) => ({
      [xField]: xValue,
      [yField]: count > 0 ? sum / count : 0
    }));
  }
}

function aggregateDataForMax(
  records: any[],
  xField: string,
  yField: string,
  seriesField: string | undefined,
  targetField: string,
  getFieldValue: Function
) {
  if (seriesField && seriesField !== yField) {
    const groupedData: { [key: string]: { [series: string]: number[] } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const seriesValue = String(record[seriesField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = {};
      }
      if (!groupedData[xValue][seriesValue]) {
        groupedData[xValue][seriesValue] = [];
      }

      groupedData[xValue][seriesValue].push(numValue);
    });

    const result: any[] = [];
    Object.entries(groupedData).forEach(([xValue, seriesData]) => {
      Object.entries(seriesData).forEach(([seriesValue, values]) => {
        const maxValue = values.length > 0 ? Math.max(...values) : 0;
        const dataItem: any = {
          [xField]: xValue,
          [yField]: maxValue,
          [seriesField]: seriesValue
        };
        result.push(dataItem);
      });
    });

    return result;
  } else {
    const groupedData: { [key: string]: number[] } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = [];
      }

      groupedData[xValue].push(numValue);
    });

    return Object.entries(groupedData).map(([xValue, values]) => {
      const maxValue = values.length > 0 ? Math.max(...values) : 0;
      return {
        [xField]: xValue,
        [yField]: maxValue
      };
    });
  }
}

function aggregateDataForMin(
  records: any[],
  xField: string,
  yField: string,
  seriesField: string | undefined,
  targetField: string,
  getFieldValue: Function
) {
  if (seriesField && seriesField !== yField) {
    const groupedData: { [key: string]: { [series: string]: number[] } } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const seriesValue = String(record[seriesField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = {};
      }
      if (!groupedData[xValue][seriesValue]) {
        groupedData[xValue][seriesValue] = [];
      }

      groupedData[xValue][seriesValue].push(numValue);
    });

    const result: any[] = [];
    Object.entries(groupedData).forEach(([xValue, seriesData]) => {
      Object.entries(seriesData).forEach(([seriesValue, values]) => {
        const minValue = values.length > 0 ? Math.min(...values) : 0;
        const dataItem: any = {
          [xField]: xValue,
          [yField]: minValue,
          [seriesField]: seriesValue
        };
        result.push(dataItem);
      });
    });

    return result;
  } else {
    const groupedData: { [key: string]: number[] } = {};

    records.forEach(record => {
      const xValue = String(record[xField] || '未知');
      const numValue = Number(record[targetField]) || 0;

      if (!groupedData[xValue]) {
        groupedData[xValue] = [];
      }

      groupedData[xValue].push(numValue);
    });

    return Object.entries(groupedData).map(([xValue, values]) => {
      const minValue = values.length > 0 ? Math.min(...values) : 0;
      return {
        [xField]: xValue,
        [yField]: minValue
      };
    });
  }
}